import styled, { keyframes, css } from 'styled-components';

// ============================================
// ANIMATIONS
// ============================================

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
  50% { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0); }
`;

export const checkPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================
// STYLED COMPONENTS - MAIN LAYOUT
// ============================================

export const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #0a0d14;
  color: #e8eaed;
  overflow: hidden;
`;

export const Header = styled.div`
  padding: 16px 24px;
  background: rgba(17, 24, 39, 0.98);
  border-bottom: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  z-index: 10;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #6366f1;
  }
`;

export const ClientName = styled.span`
  font-size: 14px;
  color: #9ca3af;
  font-weight: 400;
  padding-left: 16px;
  border-left: 1px solid rgba(75, 85, 99, 0.5);
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }
  ` : css`
    background: rgba(55, 65, 81, 0.5);
    border: 1px solid rgba(75, 85, 99, 0.5);
    color: #d1d5db;
    
    &:hover {
      background: rgba(75, 85, 99, 0.5);
      border-color: rgba(99, 102, 241, 0.5);
    }
  `}
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ProgressBadge = styled.div`
  padding: 6px 12px;
  background: ${props => {
    if (props.$percentage >= 100) return 'rgba(16, 185, 129, 0.15)';
    if (props.$percentage > 0) return 'rgba(245, 158, 11, 0.15)';
    return 'rgba(107, 114, 128, 0.15)';
  }};
  border: 1px solid ${props => {
    if (props.$percentage >= 100) return 'rgba(16, 185, 129, 0.3)';
    if (props.$percentage > 0) return 'rgba(245, 158, 11, 0.3)';
    return 'rgba(107, 114, 128, 0.3)';
  }};
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => {
    if (props.$percentage >= 100) return '#34d399';
    if (props.$percentage > 0) return '#fbbf24';
    return '#9ca3af';
  }};
`;

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// ============================================
// SECTION TABS
// ============================================

export const SectionTabs = styled.div`
  display: flex;
  gap: 2px;
  padding: 12px 24px;
  background: rgba(17, 24, 39, 0.6);
  border-bottom: 1px solid rgba(75, 85, 99, 0.2);
  overflow-x: auto;
  flex-shrink: 0;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const SectionTab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  ${props => props.$active ? css`
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.3);
  ` : css`
    background: transparent;
    color: #9ca3af;
    border: 1px solid transparent;
    
    &:hover {
      background: rgba(55, 65, 81, 0.5);
      color: #d1d5db;
    }
  `}
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  .badge {
    background: ${props => props.$active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(75, 85, 99, 0.5)'};
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
  }
`;

export const SectionContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: ${props => props.$active ? 'flex' : 'none'};
  flex-direction: column;
`;

// ============================================
// PROGRESS SECTION
// ============================================

export const FlowContainer = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 500px;
  
  .react-flow {
    width: 100%;
    height: 100%;
  }
  
  .react-flow__node {
    cursor: pointer;
  }
  
  .react-flow__edge-path {
    stroke-width: 3;
  }
  
  .react-flow__controls {
    background: rgba(17, 24, 39, 0.9);
    border: 1px solid rgba(75, 85, 99, 0.3);
    border-radius: 8px;
    /* Position controls at top-left instead of bottom-left */
    left: 10px;
    top: 10px;
    bottom: auto;
    
    button {
      background: rgba(31, 41, 55, 0.8);
      border-bottom: 1px solid rgba(75, 85, 99, 0.3);
      color: #9ca3af;
      
      &:hover {
        background: rgba(99, 102, 241, 0.2);
        color: #fff;
      }
      
      svg {
        fill: currentColor;
      }
    }
  }
  
  .react-flow__attribution {
    display: none;
  }
`;

export const ProgressSplitContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const ProgressTopPane = styled.div`
  flex: 1;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

export const ResizeDivider = styled.div`
  height: 8px;
  background: rgba(31, 41, 55, 0.8);
  border-top: 1px solid rgba(75, 85, 99, 0.4);
  border-bottom: 1px solid rgba(75, 85, 99, 0.4);
  cursor: ns-resize;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;
  
  &:hover, &:active {
    background: rgba(99, 102, 241, 0.2);
  }
  
  &::after {
    content: '';
    width: 40px;
    height: 4px;
    background: rgba(75, 85, 99, 0.6);
    border-radius: 2px;
  }
  
  &:hover::after, &:active::after {
    background: rgba(99, 102, 241, 0.6);
  }
`;

export const ProgressBottomPane = styled.div`
  height: ${props => props.$height || 280}px;
  min-height: 120px;
  max-height: 70%;
  flex-shrink: 0;
  overflow: hidden;
`;

// ============================================
// CLIENT PROFILE SECTION
// ============================================

export const ProfileSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 3px;
  }
`;

export const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

export const ProfileCard = styled.div`
  background: rgba(17, 24, 39, 0.6);
  border: 1px solid rgba(75, 85, 99, 0.3);
  border-radius: 12px;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;
`;

export const ProfileCardHeader = styled.div`
  padding: 14px 18px;
  background: rgba(99, 102, 241, 0.1);
  border-bottom: 1px solid rgba(75, 85, 99, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    width: 18px;
    height: 18px;
    color: #a5b4fc;
  }
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #e5e7eb;
    flex: 1;
  }
`;

export const CardEditButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #9ca3af;
  transition: all 0.2s ease;
  
  svg {
    width: 12px;
    height: 12px;
  }
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
    color: #a5b4fc;
  }
`;

export const ProfileCardContent = styled.div`
  padding: 16px 18px;
`;

export const ProfileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(75, 85, 99, 0.15);
  }
`;

export const ProfileLabel = styled.span`
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  margin-right: 12px;
`;

export const ProfileValue = styled.span`
  font-size: 13px;
  color: #e5e7eb;
  text-align: right;
  word-break: break-word;
`;

// ============================================
// FILES/ATTACHMENTS SECTION (styled like Documents)
// ============================================

export const FilesSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

export const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FileCard = styled.div`
  background: rgba(17, 24, 39, 0.6);
  border: 1px solid rgba(75, 85, 99, 0.3);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.3s ease;
  
  &:hover {
    background: rgba(31, 41, 55, 0.8);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

export const FileIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => {
    switch(props.$type) {
      case 'pdf': return 'rgba(239, 68, 68, 0.15)';
      case 'image': return 'rgba(59, 130, 246, 0.15)';
      case 'doc': return 'rgba(16, 185, 129, 0.15)';
      default: return 'rgba(107, 114, 128, 0.15)';
    }
  }};
  color: ${props => {
    switch(props.$type) {
      case 'pdf': return '#f87171';
      case 'image': return '#60a5fa';
      case 'doc': return '#34d399';
      default: return '#9ca3af';
    }
  }};
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

export const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #e5e7eb;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const FileDate = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

export const FileSize = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

export const FileDownloadButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(99, 102, 241, 0.1);
  color: #a5b4fc;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  ${FileCard}:hover & {
    background: rgba(99, 102, 241, 0.2);
    color: #c7d2fe;
  }
`;

// ============================================
// DOCUMENTS SECTION
// ============================================

export const DocumentsSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

export const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DocumentCard = styled.div`
  background: rgba(17, 24, 39, 0.6);
  border: 1px solid rgba(75, 85, 99, 0.3);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.3s ease;
  
  &:hover {
    background: rgba(31, 41, 55, 0.8);
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

export const DocumentIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

export const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DocumentName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #e5e7eb;
  margin-bottom: 4px;
`;

export const DocumentMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const DocumentStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  
  ${props => {
    switch(props.$status) {
      case 'complete':
      case 'verified':
        return css`
          background: rgba(16, 185, 129, 0.15);
          color: #34d399;
        `;
      case 'submitted':
        return css`
          background: rgba(59, 130, 246, 0.15);
          color: #60a5fa;
        `;
      default:
        return css`
          background: rgba(245, 158, 11, 0.15);
          color: #fbbf24;
        `;
    }
  }}
`;

export const DocumentProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ProgressBar = styled.div`
  width: 80px;
  height: 6px;
  background: rgba(55, 65, 81, 0.5);
  border-radius: 3px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: ${props => {
    if (props.$percent >= 100) return '#10b981';
    if (props.$percent >= 50) return '#6366f1';
    return '#f59e0b';
  }};
  width: ${props => props.$percent}%;
  transition: width 0.3s ease;
`;

export const ProgressText = styled.span`
  font-size: 11px;
  color: #9ca3af;
  min-width: 35px;
`;

// ============================================
// EMPTY STATE
// ============================================

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 40px;
  
  svg {
    width: 56px;
    height: 56px;
    color: rgba(99, 102, 241, 0.4);
    margin-bottom: 16px;
  }
  
  h3 {
    margin: 0 0 8px 0;
    font-size: 16px;
    color: #fff;
  }
  
  p {
    margin: 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

export const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// ============================================
// CUSTOM NODE STYLES (Progress)
// ============================================

export const StartEndNode = styled.div`
  padding: 12px 24px;
  background: ${props => props.$isEnd ? 
    'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 
    'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
  border-radius: 24px;
  color: white;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 20px ${props => props.$isEnd ? 
    'rgba(16, 185, 129, 0.3)' : 
    'rgba(99, 102, 241, 0.3)'};
  animation: ${fadeIn} 0.3s ease;
`;

export const MilestoneNodeWrapper = styled.div`
  background: rgba(17, 24, 39, 0.95);
  border: 2px solid ${props => {
    if (props.$status === 'completed') return '#10b981';
    if (props.$status === 'auto_detected') return '#ec4899';
    if (props.$status === 'in_progress') return '#6366f1';
    return 'rgba(75, 85, 99, 0.5)';
  }};
  border-radius: 12px;
  width: 300px; /* Fixed width for equal spacing between cards */
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.3s ease;
  
  ${props => props.$status === 'in_progress' && css`
    animation: ${pulse} 2s infinite;
  `}
`;

export const MilestoneHeader = styled.div`
  padding: 12px 16px;
  background: ${props => {
    if (props.$status === 'completed') return 'rgba(16, 185, 129, 0.15)';
    if (props.$status === 'auto_detected') return 'rgba(236, 72, 153, 0.15)';
    if (props.$status === 'in_progress') return 'rgba(99, 102, 241, 0.15)';
    return 'rgba(55, 65, 81, 0.5)';
  }};
  border-bottom: 1px solid rgba(75, 85, 99, 0.3);
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const MilestoneNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  background: ${props => {
    if (props.$status === 'completed') return '#10b981';
    if (props.$status === 'auto_detected') return '#ec4899';
    if (props.$status === 'in_progress') return '#6366f1';
    return 'rgba(75, 85, 99, 0.5)';
  }};
  color: white;
  
  ${props => (props.$status === 'completed' || props.$status === 'auto_detected') && css`
    animation: ${checkPulse} 0.5s ease;
  `}
`;

export const MilestoneTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => {
    if (props.$status === 'completed') return '#34d399';
    if (props.$status === 'auto_detected') return '#f472b6';
    if (props.$status === 'in_progress') return '#a5b4fc';
    return '#d1d5db';
  }};
  flex: 1;
`;

export const MilestoneDeleteButton = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 5px;
  border: none;
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.3);
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

export const StepsContainer = styled.div`
  padding: 8px 0;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
  }
`;

export const DeleteButton = styled.button`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: none;
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s ease;
  margin-left: auto;
  flex-shrink: 0;
  
  ${StepItem}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background: rgba(239, 68, 68, 0.3);
  }
  
  svg {
    width: 10px;
    height: 10px;
  }
`;

export const AddStepRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 16px;
  cursor: pointer;
  color: rgba(99, 102, 241, 0.7);
  font-size: 11px;
  transition: all 0.15s ease;
  border-top: 1px dashed rgba(75, 85, 99, 0.3);
  margin-top: 4px;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    color: #a5b4fc;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

export const StepCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  
  ${props => {
    if (props.$status === 'completed') {
      return css`
        background: #10b981;
        color: white;
        svg { width: 12px; height: 12px; }
      `;
    } else if (props.$status === 'auto_detected') {
      return css`
        background: #ec4899;
        color: white;
        svg { width: 12px; height: 12px; }
      `;
    } else if (props.$status === 'in_progress') {
      return css`
        background: transparent;
        border: 2px solid #6366f1;
        &::after {
          content: '';
          width: 8px;
          height: 8px;
          border-radius: 2px;
          background: #6366f1;
        }
      `;
    } else {
      return css`
        background: transparent;
        border: 2px solid rgba(75, 85, 99, 0.5);
      `;
    }
  }}
`;

export const StepText = styled.span`
  font-size: 12px;
  color: ${props => {
    if (props.$status === 'completed') return '#9ca3af';
    if (props.$status === 'auto_detected') return '#f9a8d4';
    if (props.$status === 'in_progress') return '#e5e7eb';
    return '#9ca3af';
  }};
  /* Text truncation for long text */
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${props => props.$status === 'completed' && css`
    text-decoration: line-through;
  `}
`;

// Timeline for steps
export const StepTimeline = styled.div`
  font-size: 10px;
  color: #6b7280;
  margin-left: auto;
  padding-right: 8px;
`;

// ============================================
// MODAL STYLES
// ============================================

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Modal = styled.div`
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  border: 1px solid rgba(75, 85, 99, 0.5);
  border-radius: 16px;
  padding: 24px;
  min-width: 380px;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
`;

export const ModalInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: rgba(31, 41, 55, 0.8);
  border: 1px solid rgba(75, 85, 99, 0.5);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

export const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 6px;
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

export const ModalButton = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
  ` : css`
    background: transparent;
    border: 1px solid rgba(75, 85, 99, 0.5);
    color: #9ca3af;
  `}
`;
